import { arrayBufferToBase64, base64ToArrayBuffer } from './webauthn'

export interface EncryptedData {
  encryptedData: string // base64
  iv: string // base64
  salt: string // base64
}

/**
 * Derive an AES-GCM key from the PRF key using PBKDF2
 * SECURITY: Only PRF keys from WebAuthn are accepted - no fallback to credential ID
 */
async function deriveEncryptionKey(
  prfKey: ArrayBuffer,
  salt: Uint8Array
): Promise<CryptoKey> {
  // Import the PRF key as raw key material
  const baseKey = await crypto.subtle.importKey(
    'raw',
    prfKey,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  )

  // Derive an AES-GCM key using PBKDF2
  const derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: 100000,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )

  return derivedKey
}

/**
 * Encrypt data using AES-GCM with a key derived from WebAuthn PRF
 * SECURITY: Only accepts PRF keys - no fallback to credential ID
 */
export async function encryptWithPRF(
  data: string,
  prfKey: ArrayBuffer
): Promise<EncryptedData> {
  // Generate random salt and IV
  const salt = new Uint8Array(16)
  crypto.getRandomValues(salt)

  const iv = new Uint8Array(12)
  crypto.getRandomValues(iv)

  // Derive encryption key from PRF
  const encryptionKey = await deriveEncryptionKey(prfKey, salt)

  // Encrypt the data
  const encoder = new TextEncoder()
  const dataBuffer = encoder.encode(data)

  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    encryptionKey,
    dataBuffer
  )

  return {
    encryptedData: arrayBufferToBase64(encryptedBuffer),
    iv: arrayBufferToBase64(iv.buffer),
    salt: arrayBufferToBase64(salt.buffer),
  }
}

/**
 * Decrypt data using AES-GCM with a key derived from WebAuthn PRF
 * SECURITY: Only accepts PRF keys - no fallback to credential ID
 */
export async function decryptWithPRF(
  encryptedData: EncryptedData,
  prfKey: ArrayBuffer
): Promise<string> {
  // Convert base64 strings back to ArrayBuffers
  const encrypted = base64ToArrayBuffer(encryptedData.encryptedData)
  const iv = new Uint8Array(base64ToArrayBuffer(encryptedData.iv))
  const salt = new Uint8Array(base64ToArrayBuffer(encryptedData.salt))

  // Derive encryption key from PRF
  const encryptionKey = await deriveEncryptionKey(prfKey, salt)

  // Decrypt the data
  try {
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      encryptionKey,
      encrypted
    )

    const decoder = new TextDecoder()
    return decoder.decode(decryptedBuffer)
  } catch (error) {
    console.error('Decryption error:', error)
    throw new Error('Failed to decrypt data. The passkey may be incorrect.')
  }
}

/**
 * Generate a random private key for Ethereum wallet
 */
export function generatePrivateKey(): string {
  const privateKeyBytes = new Uint8Array(32)
  crypto.getRandomValues(privateKeyBytes)

  // Convert to hex string with 0x prefix
  const hexString = Array.from(privateKeyBytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('')

  return `0x${hexString}`
}
